import React from 'react';
import { motion } from 'framer-motion';

interface SectionProps {
    children: React.ReactNode;
    className?: string;
    containerClassName?: string;
    id?: string;
    'aria-label'?: string;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
}

/**
 * Standard Section Component
 * Handles the standard vertical padding, responsive max-width, 
 * and theme background colors constraint.
 */
export function Section({
    children,
    className = '',
    containerClassName = '',
    id,
    'aria-label': ariaLabel,
    onMouseEnter,
    onMouseLeave,
}: SectionProps) {
    return (
        <section
            id={id}
            aria-label={ariaLabel}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            className={`w-full bg-surface-primary dark:bg-surface-dark-primary py-12 lg:py-16 transition-colors ${className}`}
        >
            <div className={`w-11/12 lg:w-[85%] max-w-7xl mx-auto flex flex-col ${containerClassName}`}>
                {children}
            </div>
        </section>
    );
}

/**
 * Motion Section Component
 * Wraps the standard Section with a smooth fade-in-up entrance animation.
 */
export function MotionSection({
    children,
    className = '',
    containerClassName = '',
    id,
    'aria-label': ariaLabel,
}: SectionProps) {
    return (
        <section
            id={id}
            aria-label={ariaLabel}
            className={`w-full bg-surface-primary dark:bg-surface-dark-primary py-12 lg:py-16 overflow-hidden transition-colors ${className}`}
        >
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className={`w-11/12 lg:w-[85%] max-w-7xl mx-auto flex flex-col ${containerClassName}`}
            >
                {children}
            </motion.div>
        </section>
    );
}
